import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Slider } from './ui/slider';
import { cn } from '../lib/utils';

// General organizational maturity descriptions (aggregated across L&D functions)
const GENERAL_MATURITY_LEVELS = {
  emerging: {
    range: '0-24',
    label: 'Emerging',
    color: 'bg-slate-100 text-slate-700 border-slate-300',
    activeColor: 'bg-slate-200 border-slate-400',
    description: 'Basic L&D operations with reactive, ad-hoc processes. No formal frameworks, limited measurement, manual administration, and learning is seen as compliance-driven rather than strategic.'
  },
  developing: {
    range: '25-49',
    label: 'Developing',
    color: 'bg-amber-50 text-amber-700 border-amber-300',
    activeColor: 'bg-amber-100 border-amber-400',
    description: 'Some documented processes exist but adoption is inconsistent. Basic competency definitions for some roles, simple scheduling tools, descriptive analytics, and manager engagement is limited.'
  },
  managed: {
    range: '50-74',
    label: 'Managed',
    color: 'bg-teal-50 text-teal-700 border-teal-300',
    activeColor: 'bg-teal-100 border-teal-400',
    description: 'Standardized practices with defined governance. Comprehensive skills taxonomy, centralized scheduling, diagnostic analytics with KPI correlations, and moderate voluntary adoption for key programs.'
  },
  optimized: {
    range: '75-100',
    label: 'Optimized',
    color: 'bg-cyan-50 text-cyan-700 border-cyan-300',
    activeColor: 'bg-cyan-100 border-cyan-400',
    description: 'Predictive, AI-enhanced operations with continuous optimization. Real-time gap dashboards, intelligent automation, prescriptive analytics with causal attribution, and learning embedded in daily work.'
  }
};

// Pillar-specific maturity descriptions
const PILLAR_MATURITY_LEVELS = {
  learning_demand: {
    title: 'Learning Demand & Productization',
    subtitle: 'Content portfolio management, lifecycle governance, metadata quality, and value proposition clarity.',
    levels: {
      emerging: "Content is added reactively with no structure. Duplication is common, metadata is poor, and there's no clear value articulation or lifecycle management.",
      developing: "Some categorization exists but inconsistently applied. Basic descriptions are available, search is unreliable, and content reviews happen ad-hoc.",
      managed: "Portfolio categories are defined with standardized metadata. Regular lifecycle reviews occur, and basic marketing campaigns target key programs.",
      optimized: "Portfolio is strategically curated with AI-powered discovery. Content is product-managed with measured value delivery, predictive needs analysis, and continuous optimization."
    }
  },
  skills_alignment: {
    title: 'Skills & Capability Alignment',
    subtitle: 'Skills framework maturity, gap visibility, learning-to-skill mapping, and workforce readiness.',
    levels: {
      emerging: "No formal skills framework exists. Gaps are unknown, learning isn't mapped to skills, and there's no workforce readiness measurement.",
      developing: "Basic job competencies are defined for some roles. Managers report gaps informally, and some content is tagged to skills.",
      managed: "Comprehensive skills taxonomy covers all roles. Annual assessments identify gaps, learning is systematically mapped to skills, and quarterly reviews align to strategy.",
      optimized: "AI-enhanced skills ontology integrates with labor market data. Real-time gap dashboards drive automated remediation, and predictive readiness modeling supports strategic initiatives."
    }
  },
  governance_operations: {
    title: 'Governance & Operational Efficiency',
    subtitle: 'Operations standardization, automation level, delivery scalability, and cost management.',
    levels: {
      emerging: "No standard processes—each team operates independently. Scheduling is manual with conflicts, all admin is manual, and costs are invisible.",
      developing: "Some documented processes exist but adoption is inconsistent. Basic scheduling tools are used, simple automations handle registrations.",
      managed: "Standardized processes with compliance checks. Centralized scheduling tracks utilization, workflow automation handles common tasks, and costs are tracked by program.",
      optimized: "Self-optimizing operations with exception-based management. AI-driven resource allocation, intelligent automation with chatbot support, and predictive cost modeling drive value-based budgeting."
    }
  },
  insight_value: {
    title: 'Insight & Value Demonstration',
    subtitle: 'Analytics maturity, KPI linkage, executive reporting, and ROI visibility.',
    levels: {
      emerging: "Only basic LMS reports (completions). No linkage to business KPIs, no executive reporting, no ROI calculations.",
      developing: "Descriptive analytics with manual reporting. Anecdotal business connections, basic activity reports on request, satisfaction surveys only.",
      managed: "Diagnostic analytics with standardized dashboards. Defined correlations for key programs, regular trend reports, and Kirkpatrick L1-L2 measurement.",
      optimized: "Prescriptive analytics with AI-driven recommendations. Causal attribution models validate business impact, real-time executive dashboards provide predictive insights, and continuous A/B testing optimizes value."
    }
  },
  adoption_impact: {
    title: 'Adoption & Behavioral Impact',
    subtitle: 'Learner adoption rates, manager reinforcement, behavior change measurement, and learning culture.',
    levels: {
      emerging: "Low adoption—learning is seen as a burden. Managers don't engage, no behavior change measurement, skills decay quickly.",
      developing: "Compliance-driven adoption only. Managers approve requests but don't follow up, self-reported behavior surveys, refresher training has low uptake.",
      managed: "Moderate voluntary adoption for key programs. Managers discuss learning in reviews, multi-rater behavior assessments, and spaced reinforcement for critical skills.",
      optimized: "Learning embedded in daily work with high self-directed adoption. Managers measured on development outcomes, behavioral analytics capture evidence, and a performance support ecosystem ensures sustained capability."
    }
  }
};

// Get the current maturity level based on value
const getMaturityLevel = (value) => {
  if (value < 25) return 'emerging';
  if (value < 50) return 'developing';
  if (value < 75) return 'managed';
  return 'optimized';
};

// Get maturity label for display
const getMaturityLabel = (value) => {
  const level = getMaturityLevel(value);
  return GENERAL_MATURITY_LEVELS[level].label;
};

export const MaturitySliderWithGuide = ({
  value,
  onChange,
  pillarType = null, // null for general, or one of: learning_demand, skills_alignment, governance_operations, insight_value, adoption_impact
  showTitle = true,
  className = '',
  testId = 'maturity-slider'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const currentLevel = getMaturityLevel(value);
  const levels = GENERAL_MATURITY_LEVELS;
  const pillarInfo = pillarType ? PILLAR_MATURITY_LEVELS[pillarType] : null;
  
  // Get description based on whether it's pillar-specific or general
  const getDescription = (levelKey) => {
    if (pillarInfo) {
      return pillarInfo.levels[levelKey];
    }
    return levels[levelKey].description;
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Header with title and current value */}
      {showTitle && (
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-slate-800">
              {pillarInfo ? pillarInfo.title : 'Value Realisation Maturity'}
            </h4>
            {pillarInfo && (
              <p className="text-xs text-slate-500 mt-0.5">{pillarInfo.subtitle}</p>
            )}
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-slate-800">{value}</span>
            <p className={cn(
              "text-sm font-medium",
              currentLevel === 'emerging' && 'text-slate-600',
              currentLevel === 'developing' && 'text-amber-600',
              currentLevel === 'managed' && 'text-teal-600',
              currentLevel === 'optimized' && 'text-cyan-600'
            )}>
              {getMaturityLabel(value)}
            </p>
          </div>
        </div>
      )}

      {/* Slider with gradient background */}
      <div className="relative pt-2">
        <div className="absolute inset-x-0 top-4 h-2 rounded-full bg-gradient-to-r from-slate-300 via-amber-300 via-teal-400 to-cyan-400 opacity-30" />
        <Slider
          value={[value]}
          onValueChange={([val]) => onChange(val)}
          max={100}
          min={0}
          step={5}
          className="relative z-10"
          data-testid={testId}
        />
      </div>

      {/* Level markers */}
      <div className="flex justify-between text-xs text-slate-500 px-1">
        <span className={currentLevel === 'emerging' ? 'font-medium text-slate-700' : ''}>
          0 - Emerging
        </span>
        <span className={currentLevel === 'developing' ? 'font-medium text-amber-700' : ''}>
          25 - Developing
        </span>
        <span className={currentLevel === 'managed' ? 'font-medium text-teal-700' : ''}>
          50 - Managed
        </span>
        <span className={currentLevel === 'optimized' ? 'font-medium text-cyan-700' : ''}>
          75 - Optimized
        </span>
        <span className="text-slate-400">100</span>
      </div>

      {/* Expandable guide section */}
      <div className="mt-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 transition-colors"
          data-testid={`${testId}-guide-toggle`}
        >
          <HelpCircle className="h-4 w-4" />
          <span>What does this mean?</span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3 animate-in slide-in-from-top-2 duration-200">
            {pillarInfo && (
              <div className="pb-3 border-b border-slate-200">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <span className="text-lg">ℹ️</span>
                  What We Measure
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {pillarInfo.subtitle}
                </p>
              </div>
            )}
            
            <p className="text-sm font-medium text-slate-700">Understanding the Maturity Levels</p>
            
            <div className="space-y-2">
              {Object.entries(levels).map(([key, level]) => (
                <div
                  key={key}
                  className={cn(
                    "flex gap-3 p-2 rounded-lg border transition-all",
                    currentLevel === key ? level.activeColor : level.color,
                    currentLevel === key && "ring-2 ring-offset-1",
                    currentLevel === 'emerging' && key === 'emerging' && "ring-slate-400",
                    currentLevel === 'developing' && key === 'developing' && "ring-amber-400",
                    currentLevel === 'managed' && key === 'managed' && "ring-teal-400",
                    currentLevel === 'optimized' && key === 'optimized' && "ring-cyan-400"
                  )}
                >
                  <div className={cn(
                    "flex-shrink-0 min-w-[5.5rem] py-1.5 px-2 rounded text-center text-xs font-medium border",
                    level.color
                  )}>
                    <div>{level.range}:</div>
                    <div>{level.label}</div>
                  </div>
                  <p className={cn(
                    "text-sm flex-1",
                    currentLevel === key ? "text-slate-800" : "text-slate-600"
                  )}>
                    {getDescription(key)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Export helper functions for use elsewhere
export { getMaturityLevel, getMaturityLabel, GENERAL_MATURITY_LEVELS, PILLAR_MATURITY_LEVELS };

export default MaturitySliderWithGuide;
